import { useState, useEffect, useCallback } from 'react';

type StorageType = 'localStorage' | 'sessionStorage';
const LOCAL_STORAGE_TYPE: StorageType = 'localStorage';
const SESSION_STORAGE_TYPE: StorageType = 'sessionStorage';

type SetAction<S> = S | ((prevState: S) => S);

function getItem<T>(key: string, defaultValue: T, storageType: StorageType) {
  try {
    // Get from storage by key
    const item = window[storageType].getItem(key);

    // Parse stored json or if none return defaultValue
    return item ? (JSON.parse(item) as T) : defaultValue;
  } catch (error) {
    // fallback to defaultValue on error
    return defaultValue;
  }
}

export function useStorage<T>(
  key: string,
  defaultValue: T,
  storageType: StorageType,
): [T, (value: SetAction<T>) => void] {
  const [storedValue, setStoredValue] = useState(() => getItem(key, defaultValue, storageType));

  useEffect(() => {
    window[storageType].setItem(key, JSON.stringify(storedValue));
  }, [key, storageType, storedValue]);

  const setValue = useCallback((value: SetAction<T>) => {
    // update state, storage update will happen after in an effect
    setStoredValue(prev => (value instanceof Function ? value(prev) : value));
  }, []);

  return [storedValue, setValue];
}

/**
 * Wraps React's useState API additionally persisting to browser local storage
 * default value will be used as the initial value if the item is not found in local storage
 * @param key storage key to use
 * @param defaultValue default value that will be set if key not in use
 * @returns value stored in local storage
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
): [T, (value: SetAction<T>) => void] {
  return useStorage(key, defaultValue, LOCAL_STORAGE_TYPE);
}

/**
 * Wraps React's useState API additionally persisting to browser session storage
 * default value will be used as the initial value if the item is not found in session storage
 * @param key storage key to use
 * @param defaultValue default value that will be set if key not in use
 * @returns value stored in session storage
 */
export function useSessionStorage<T>(
  key: string,
  defaultValue: T,
): [T, (value: SetAction<T>) => void] {
  return useStorage(key, defaultValue, SESSION_STORAGE_TYPE);
}
